import { isFamilyLabel } from '../../../packages/domain/src/02-social.mjs';
import { createSocialControls } from '../ui/212-social-controls.mjs';
import { canReceivePersonalConnections } from '../../../packages/domain/src/08-profile.mjs';
import { createDirectChoices } from '../ui/15-direct-choices.mjs';
import { openProfileEditor } from '../ui/10-profile-editor.mjs';
import { createProfileImage } from '../ui/11-profile-image.mjs';
import { createContacts } from './11-contacts.mjs';
// Owns people rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create discovery, relationship and profile views using injected snapshot access. */
export function createPeople(ctx) {
  const { openContacts } = createContacts(ctx);
  const social = createSocialControls(ctx);
  let connectionFilter = 'all';
  function personCard(user) {
    const card = ctx.e('article', 'person-card');
    card.append(
      ctx.avatar(user),
      ctx.append(ctx.e('h2'), ctx.link(user.name, `#/profile/${user.id}`)),
      ctx.e('span', 'post-meta', user.location),
      ctx.e('p', '', user.bio),
    );
    const shared = user.shared ?? ctx.sharedInterests(ctx.state.me.interests, user.interests);
    card.append(
      ctx.e(
        'p',
        'match-reason',
        shared.length ? `You both enjoy ${shared.join(', ')}.` : 'A new perspective to explore.',
      ),
      ctx.e('div', 'interest-tags', user.interests),
    );
    if (user.mutualCount)
      card.append(
        ctx.e('p', 'match-reason', ctx.translate('social.mutual', { count: user.mutualCount })),
      );
    const actions = ctx.e('div', 'button-row');
    const friend = ctx.state.friends.includes(user.id);
    const blocked = ctx.state.settings.blocked.includes(user.id);
    actions.append(ctx.link('View profile', `#/profile/${user.id}`, 'secondary-button'));
    if (friend && !blocked && canReceivePersonalConnections(user)) {
      const selected = ctx.state.settings.closeFriends.includes(user.id);
      const closeFriend = ctx.button(selected ? 'Close friend' : 'Add to circle', () =>
        ctx.command({ type: 'close-friend', id: user.id, selected: !selected }),
      );
      closeFriend.prepend(ctx.icon('star'));
      closeFriend.setAttribute('aria-pressed', String(selected));
      actions.append(
        closeFriend,
        ctx.button(
          ctx.translate(
            `social.label.${ctx.state.settings.connectionLabels?.[user.id] ?? 'friend'}`,
          ),
          () => social.editConnection(user.id),
          'secondary-button',
        ),
      );
    } else if (!blocked) actions.append(...relationshipActions(user.id, user));
    else
      actions.append(
        ctx.button('Unblock', () => ctx.command({ type: 'block', id: user.id, blocked: false })),
      );
    card.append(actions);
    return card;
  }

  function searchForm() {
    const form = ctx.e('form', 'people-search');
    const input = ctx.field('input', {
      value: ctx.query,
      placeholder: ctx.uiText('Search names, handles, interests or places'),
      maxLength: 120,
      ariaLabel: 'Search this view',
    });
    const submit = ctx.e('button', 'secondary-button', 'Search');
    form.append(input, submit);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      ctx.query = input.value.trim();
      document.querySelector('#search').value = ctx.query;
      ctx.render();
    });
    return form;
  }

  function renderPeople(discover = false) {
    ctx.view.append(
      ctx.heading(
        discover ? 'Meet your next people.' : 'Your people.',
        discover
          ? 'Find new people through interests you choose to share.'
          : 'Your friends and close circle, together.',
      ),
      searchForm(),
      ctx.iconButton('users', 'Find friends from contacts', openContacts),
      ctx.button(ctx.translate('referral.invite'), () => ctx.openInvite?.()),
    );
    if (ctx.state.settings.socialGuide?.version !== 1) ctx.view.append(social.guideEntry());
    else
      ctx.view.append(
        ctx.iconButton('circle-help', ctx.translate('social.guide'), social.openGuide),
      );
    if (!discover) {
      renderRequests();
      ctx.view.append(ctx.link('Find new people', '#/discover', 'secondary-button'));
      const filters = createDirectChoices(ctx, {
        label: ctx.translate('social.connections'),
        name: 'connection-filter',
        variant: 'filter',
        value: connectionFilter,
        options: [
          {
            value: 'all',
            label: ctx.translate('social.audience.friends'),
            literal: true,
            icon: 'users',
          },
          {
            value: 'family',
            label: ctx.translate('social.familyAudience'),
            literal: true,
            icon: 'heart',
          },
          { value: 'close', label: 'Close circle', icon: 'star' },
        ],
        onSelect: (value) => {
          connectionFilter = value;
          ctx.render();
        },
      });
      ctx.view.append(filters.node);
      const users = ctx.state.users.filter(
        (u) =>
          u.id !== ctx.state.me.id &&
          ctx.state.friends.includes(u.id) &&
          (connectionFilter !== 'family' ||
            isFamilyLabel(ctx.state.settings.connectionLabels?.[u.id])) &&
          (connectionFilter !== 'close' || ctx.state.settings.closeFriends.includes(u.id)) &&
          !ctx.state.settings.blocked.includes(u.id) &&
          `${u.name} ${u.handle ?? ''} ${u.interests} ${u.location}`
            .toLowerCase()
            .includes(ctx.query.toLowerCase()),
      );
      ctx.view.append(
        ctx.sectionTop(
          connectionFilter === 'family'
            ? ctx.translate('social.familyAudience')
            : connectionFilter === 'close'
              ? ctx.uiText('Close circle')
              : ctx.uiText('Friends'),
          String(users.length),
        ),
      );
      const grid = ctx.e('div', 'people-grid');
      users.forEach((u) => grid.append(personCard(u)));
      ctx.view.append(
        users.length ? grid : ctx.empty('No matching friends. Try Discover to find new people.'),
      );
      return;
    }
    const filters = ctx.e('div', 'discovery-filters');
    const scope = createDirectChoices(ctx, {
      label: ctx.uiText('People filter'),
      name: 'people-filter',
      variant: 'filter',
      value: ctx.discoveryScope,
      options: [
        { value: 'new', label: 'New people', icon: 'users' },
        { value: 'all', label: 'Everyone', icon: 'globe' },
        {
          value: 'mutual',
          label: ctx.translate('social.mutualFilter'),
          literal: true,
          icon: 'share-2',
        },
      ],
      onSelect: (value) => {
        ctx.discoveryScope = value;
        ctx.render();
      },
    });
    const interest = createDirectChoices(ctx, {
      label: ctx.uiText('Interest filter'),
      name: 'interest-filter',
      variant: 'filter',
      searchable: true,
      value: ctx.discoveryInterest,
      options: [
        { value: '', label: 'All interests', icon: 'sparkles' },
        ...[...new Set(ctx.state.users.flatMap((u) => ctx.interests(u.interests)))]
          .sort()
          .map((value) => ({ value, label: value, literal: true, icon: 'hash' })),
      ],
      onSelect: (value) => {
        ctx.discoveryInterest = value;
        ctx.render();
      },
    });
    filters.append(
      scope.node,
      interest.node,
      ctx.button(ctx.discoveryShared ? 'Shared interests only ✓' : 'My shared interests', () => {
        ctx.discoveryShared = !ctx.discoveryShared;
        ctx.discoveryScope = 'new';
        ctx.render();
      }),
    );
    ctx.view.append(filters);
    const results = ctx.e('div', 'people-grid');
    results.id = 'discovery-results';
    results.append(ctx.e('p', 'subtle', 'Finding people…'));
    ctx.view.append(results);
    const generation = ++ctx.discoveryGeneration;
    ctx
      .api(
        `/api/discover?query=${encodeURIComponent(ctx.query.replace(/^#/, ''))}&interest=${encodeURIComponent(ctx.discoveryInterest)}&scope=${ctx.discoveryScope}&sharedOnly=${ctx.discoveryShared}`,
      )
      .then((users) => {
        if (generation !== ctx.discoveryGeneration || ctx.route()[0] !== 'discover') return;
        results.replaceChildren(...users.map(personCard));
        if (!users.length)
          results.append(ctx.empty('No matches yet. Try Everyone or another interest.'));
      })
      .catch((error) => {
        if (results.isConnected)
          results.replaceChildren(
            ctx.e('p', 'form-error', error.message),
            ctx.button('Try again', ctx.render),
          );
      });
    const posts = ctx.state.posts
      .filter(
        (p) =>
          p.audience === 'public' &&
          (!p.expiresAt || p.expiresAt > Date.now()) &&
          (!ctx.query ||
            `${p.text} ${p.location} ${ctx.who(p.authorId).interests}`
              .toLowerCase()
              .includes(ctx.query.replace(/^#/, '').toLowerCase())),
      )
      .sort((a, b) => b.createdAt - a.createdAt);
    ctx.view.append(ctx.sectionTop('Public moments', `${posts.length} posts`));
    posts
      .slice(0, 6)
      .forEach((p) =>
        ctx.view.append(ctx.postCard(p, { reason: 'A public moment from your community' })),
      );
  }

  function renderProfile(id) {
    const user = id === ctx.state.me.id ? ctx.state.me : ctx.who(id);
    if (!user) {
      social.loadProfile(id);
      return;
    }
    const self = id === ctx.state.me.id;
    const blocked = ctx.state.settings.blocked.includes(id);
    const friend = ctx.state.friends.includes(id);
    const cover = ctx.e(
      'div',
      user.coverImage ? 'profile-cover' : 'profile-cover profile-cover-empty',
    );
    if (user.coverImage)
      cover.append(
        ctx.field('img', {
          src: ctx.photoSource(user.coverImage),
          alt: ctx.uiText('Profile cover'),
        }),
      );
    ctx.view.append(cover);
    const detail = ctx.e('section', 'profile-details');
    const identity = ctx.e('div', 'profile-identity');
    const name = ctx.e('div', 'profile-name');
    name.append(ctx.e('h1', '', user.name));
    if (user.handle) name.append(ctx.e('p', 'profile-handle', `@${user.handle}`));
    const metadata = [
      user.location,
      user.pronouns,
      self ? ctx.uiText('Your profile') : user.isDemo ? ctx.uiText('Fictional demo member') : '',
    ].filter(Boolean);
    if (metadata.length) name.append(ctx.e('p', 'post-meta', metadata.join(' · ')));
    const actions = ctx.e('div', 'profile-actions');
    if (self)
      actions.append(
        ctx.iconButton('pencil', 'Edit profile', editProfile),
        ctx.iconButton('star', 'Customize badges', editBadges),
        ctx.iconButton('plus', 'Share a moment', () => ctx.composer()),
      );
    else {
      if (blocked)
        actions.append(
          ctx.button('Unblock', () => ctx.command({ type: 'block', id, blocked: false })),
        );
      else actions.append(...relationshipActions(id));
      if (friend && !blocked && canReceivePersonalConnections(user))
        actions.append(
          ctx.iconButton('message-circle', 'Message', () => ctx.go(`messages/${id}`)),
          ctx.iconButton(
            'star',
            ctx.state.settings.closeFriends.includes(id) ? 'Close friend' : 'Add to circle',
            () =>
              ctx.command({
                type: 'close-friend',
                id,
                selected: !ctx.state.settings.closeFriends.includes(id),
              }),
          ),
        );
      if (!blocked)
        actions.append(
          ctx.iconButton('shield-off', 'Block', () =>
            ctx.command({ type: 'block', id, blocked: true }),
          ),
        );
    }
    if (friend && !self && !blocked)
      actions.append(
        ctx.button(
          ctx.translate(`social.label.${ctx.state.settings.connectionLabels?.[id] ?? 'friend'}`),
          () => social.editConnection(id),
          'secondary-button',
        ),
      );
    if (!blocked && (self || user.canBrowseConnections !== false))
      actions.append(
        ctx.iconButton('users', ctx.translate('social.connections'), () =>
          social.openConnections(id),
        ),
      );
    identity.append(ctx.avatar(user, true), name, actions);
    detail.append(identity);
    if (user.bio) detail.append(ctx.e('p', 'profile-bio', user.bio));
    if (user.interests) detail.append(ctx.e('p', 'profile-interests', user.interests));
    if (user.websites?.length) {
      const websites = ctx.e('div', 'profile-links');
      for (const website of user.websites) {
        const link = ctx.link(website, website);
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        websites.append(link);
      }
      detail.append(websites);
    }
    if (self) {
      const stats = ctx.e('div', 'profile-stats');
      for (const [count, label, href] of [
        [ctx.state.friends.length, 'Friends', '#/friends'],
        [ctx.state.following.length, 'Following'],
        [ctx.state.followerCount, 'Followers'],
      ]) {
        const stat = href ? ctx.link('', href) : ctx.e('span');
        stat.append(ctx.e('strong', '', String(count)), ctx.e('span', '', ctx.uiText(label)));
        stats.append(stat);
      }
      detail.append(stats);
    }
    if (user.badges?.length) detail.append(profileBadges(user));
    ctx.view.append(
      detail,
      ctx.sectionTop(
        self ? 'Your moments' : 'Their moments',
        friend && !self ? 'Connected as friends' : '',
      ),
    );
    if (user.courses?.length && !blocked) {
      const courses = ctx.e('section', 'settings-card');
      courses.append(ctx.e('h2', '', 'Ordered learning guides'));
      for (const course of user.courses)
        courses.append(
          ctx.link(
            `${course.title}${course.previewOnly ? ' · owner draft preview' : ` · ${course.publishedLessons} published lessons`}`,
            `#/course/${course.id}`,
            'secondary-button',
          ),
        );
      ctx.view.append(courses);
    }
    const posts = ctx.state.posts
      .filter((p) => p.authorId === id)
      .sort((a, b) => b.createdAt - a.createdAt);
    if (blocked)
      ctx.view.append(ctx.empty('This member is blocked. Unblock them to see their posts.'));
    else if (!posts.length) ctx.view.append(ctx.empty('No shared moments to show yet.'));
    else {
      const moments = ctx.e('div', 'profile-moments');
      let shown = 0;
      const more = ctx.button('Load more', () => appendBatch(), 'secondary-button');
      function appendBatch() {
        posts.slice(shown, shown + 12).forEach((post) => moments.append(ctx.postCard(post)));
        shown += 12;
        more.hidden = shown >= posts.length;
      }
      appendBatch();
      ctx.view.append(moments, more);
    }
  }

  function profileBadges(user) {
    const row = ctx.e('div', 'profile-badges');
    for (const badge of user.badges ?? []) {
      const b = ctx.e('span', 'profile-badge');
      if (badge.image)
        b.append(
          ctx.field('img', {
            src: ctx.photoSource(badge.image, 160),
            alt: badge.label,
            loading: 'lazy',
            width: 28,
            height: 28,
          }),
        );
      else b.append(ctx.e('span', 'badge-emoji', badge.emoji));
      b.append(ctx.e('span', '', badge.label));
      row.append(b);
    }
    return row;
  }

  function editBadges() {
    const form = ctx.e('form', 'form-stack');
    const existing = ctx.state.me.badges ?? [];
    const selected = existing.map((b) => ({ ...b }));
    const preview = ctx.e('div', 'profile-badges');
    const redraw = () => {
      preview.replaceChildren();
      for (const [i, b] of selected.entries()) {
        const remove = ctx.button(
          (b.emoji ?? 'Image') + ' ' + b.label + ' ×',
          () => {
            selected.splice(i, 1);
            redraw();
          },
          'secondary-button',
        );
        remove.setAttribute('aria-label', 'Remove badge ' + b.label);
        preview.append(remove);
      }
    };
    redraw();
    form.append(
      ctx.e(
        'p',
        'subtle',
        'Your badges are visible on your profile. Choose what you want to express. They do not change your feed ranking.',
      ),
      preview,
    );
    const label = ctx.field('input', {
      placeholder: 'Badge label (up to 40 characters)',
      maxLength: 40,
      ariaLabel: 'Badge label',
    });
    form.append(label);
    const add = (b) => {
      if (selected.length >= 6) {
        ctx.toast('Choose up to six badges.');
        return;
      }
      if (!label.value.trim()) {
        ctx.toast('Add a badge label first.');
        label.focus();
        return;
      }
      selected.push({ ...b, label: label.value.trim() });
      label.value = '';
      redraw();
    };
    for (const [name, emojis] of [
      ['Care & inclusion', ['🤝', '💙', '♿', '🫶']],
      ['Planet & nature', ['🌍', '🌱', '🐝', '♻️']],
      ['Culture & community', ['🎨', '📚', '🎵', '🏳️‍🌈']],
    ]) {
      const pack = ctx.e('div', 'badge-pack');
      pack.append(ctx.e('h3', '', name));
      const buttons = ctx.e('div', 'badge-emoji-options');
      for (const emoji of emojis) {
        const b = ctx.button(emoji, () => add({ emoji }));
        b.setAttribute('aria-label', `Add ${emoji} badge using your label`);
        buttons.append(b);
      }
      pack.append(buttons);
      form.append(pack);
    }
    const images = ctx.e('div', 'photo-choices');
    for (const [name, caption] of Object.entries(ctx.imageNames)) {
      const b = ctx.button('', () => add({ image: `/assets/${name}.png` }), 'photo-choice');
      b.setAttribute('aria-label', 'Use ' + caption + ' as an image badge');
      b.append(ctx.field('img', { src: `/assets/${name}-160.webp`, alt: caption }));
      images.append(b);
    }
    form.append(ctx.e('h3', '', 'Image badges'), images);
    const badgeImage = createProfileImage(ctx, ctx.profileImageTools, {
      label: 'Image badge',
      ratio: 1,
    });
    form.append(
      ctx.e('h3', '', ctx.uiText('Use your own badge image')),
      badgeImage.node,
      ctx.button('Add edited image badge', async () => {
        if (!label.value.trim()) {
          ctx.toast('Add a badge label first.');
          return;
        }
        if (!badgeImage.hasImage()) {
          ctx.toast('Choose one photo for your badge.');
          return;
        }
        try {
          const upload = await badgeImage.exportUpload();
          add({ upload });
          badgeImage.clear();
        } catch (error) {
          ctx.toast(ctx.uiText(error.message));
        }
      }),
    );
    ctx.modal.addEventListener('close', () => badgeImage.dispose(), { once: true });
    const save = ctx.e('button', 'primary-button', 'Save badges');
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    form.append(
      error,
      ctx.append(
        ctx.e('div', 'dialog-actions'),
        ctx.button('Cancel', () => ctx.modal.close()),
        save,
      ),
    );
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      save.disabled = true;
      try {
        await ctx.command({ type: 'profile-badges', badges: selected });
        ctx.modal.close();
        ctx.toast('Profile badges updated.');
      } catch (err) {
        error.textContent = err.message;
      } finally {
        save.disabled = false;
      }
    });
    ctx.dialog('Make your profile yours', form);
  }

  function editProfile() {
    openProfileEditor(ctx);
  }

  function relationshipActions(id, member = ctx.who(id)) {
    const actions = [];
    const following = ctx.state.following.includes(id);
    actions.push(
      ctx.button(
        following ? 'Following · Unfollow' : 'Follow',
        () => ctx.command({ type: 'follow', id, following: !following }),
        following ? 'secondary-button' : 'primary-button',
      ),
    );
    if (!canReceivePersonalConnections(member)) return actions;
    if (ctx.state.friends.includes(id)) {
      actions.push(
        ctx.button(
          'Remove friend',
          () =>
            ctx.dialog(
              'Remove friend?',
              ctx.append(
                ctx.e('div', 'form-stack'),
                ctx.e('p', '', 'Friend-only access and close-circle membership will be removed.'),
                ctx.button(
                  'Remove friend',
                  async () => {
                    await ctx.command({ type: 'friend', id, action: 'remove' });
                    ctx.modal.close();
                  },
                  'primary-button',
                ),
              ),
            ),
          'text-button',
        ),
      );
      return actions;
    }
    const incoming = ctx.state.friendRequests.find(
      (r) => r.status === 'pending' && r.from === id && r.to === ctx.state.me.id,
    );
    const outgoing = ctx.state.friendRequests.find(
      (r) => r.status === 'pending' && r.to === id && r.from === ctx.state.me.id,
    );
    if (incoming)
      actions.push(
        ctx.button('Accept request', () =>
          ctx.command({
            type: 'friend-request',
            id: incoming.id,
            action: 'accept',
          }),
        ),
        ctx.button(
          'Decline',
          () =>
            ctx.command({
              type: 'friend-request',
              id: incoming.id,
              action: 'decline',
            }),
          'text-button',
        ),
      );
    else if (outgoing)
      actions.push(
        ctx.button(
          'Cancel request',
          () =>
            ctx.command({
              type: 'friend-request',
              id: outgoing.id,
              action: 'cancel',
            }),
          'text-button',
        ),
      );
    else if (member?.canRequest !== false)
      actions.push(
        ctx.button(
          'Add friend',
          () => ctx.command({ type: 'friend', id, action: 'request' }),
          'secondary-button',
        ),
      );
    return actions;
  }

  function renderRequests() {
    const pending = ctx.state.friendRequests.filter((r) => r.status === 'pending');
    if (!pending.length) return;
    ctx.view.append(ctx.sectionTop('Friend requests', `${pending.length} pending`));
    const requests = ctx.e('div', 'request-list');
    for (const r of pending) {
      const incoming = r.to === ctx.state.me.id;
      const id = incoming ? r.from : r.to;
      const row = ctx.e('div', 'request-card');
      row.append(
        ctx.avatar(ctx.who(id)),
        ctx.append(
          ctx.e('div'),
          ctx.link(ctx.who(id).name, `#/profile/${id}`),
          ctx.e(
            'p',
            'subtle',
            incoming ? 'Wants to connect with you' : 'Waiting for their acceptance',
          ),
        ),
      );
      const actions = ctx.e('div', 'button-row');
      if (incoming)
        actions.append(
          ctx.button('Accept', () =>
            ctx.command({ type: 'friend-request', id: r.id, action: 'accept' }),
          ),
          ctx.button('Decline', () =>
            ctx.command({
              type: 'friend-request',
              id: r.id,
              action: 'decline',
            }),
          ),
        );
      else
        actions.append(
          ctx.button('Cancel', () =>
            ctx.command({ type: 'friend-request', id: r.id, action: 'cancel' }),
          ),
        );
      row.append(actions);
      requests.append(row);
    }
    ctx.view.append(requests);
  }
  return {
    personCard,
    searchForm,
    renderPeople,
    renderProfile,
    profileBadges,
    editBadges,
    editProfile,
    relationshipActions,
    renderRequests,
    openContacts,
  };
}
